import { load as loadGlobals } from '@/api/globals'
import { create as createPlatform } from '@/api/platform'
import App from '@/components/App'

;(async function () {
  // If we don't pass any arguments here then only the required globals will be loaded
  await loadGlobals()

  // Since the system will be loading components built with their own versions of the Svelte internals it means we
  // can't share data around easily using context, so instead the platform object will contain all shared data and
  // API functions etc.
  // Because of this we need to make sure this is set up correctly before instantiating the main application
  //
  // We expect that the server-side rendering process has included a script tag on the page with a JSON string
  // that represents all of the store data that was used to generate the markup
  // Since we want our JavaScript to take over using the same data we need to initialise our client-side store using
  // that object, otherwise the data on the page will change as soon as the main application takes over
  const platform = window.platform = await createPlatform(window.__STORE_DATA__, {
    host: location.host,
    pathname: location.pathname,
    query: location.search,
    hash: location.hash,
  })

  await platform.api.core.loadHandlers()

  const Entry = await platform.api.core.selectEntry(platform.storeData.config.site.routes)

  await platform.api.core.loadComponents(platform.storeData.components, {
    missing: platform.storeData.config.components.missing,
  })

  if (platform.storeData.render.plugins) {
    const initialisedPlugins = await platform.api.core.initPlugins(platform.storeData.render.plugins)

    platform.store.update(store => {
      store.render.plugins = initialisedPlugins

      return store
    })
  }

  let initHandle = null
  const init = async () => {
    // Clear any default initialisation timeout
    clearTimeout(initHandle)
    initHandle = null

    const observer = new PerformanceObserver(list => {
      const entries = list.getEntries()
      const last = entries[entries.length - 1]
      const src = last.element?.getAttribute('src')

      if (src) {
        platform.api.cms.perf.lcp(platform.router.pathname + platform.router.query, src)
      }
    })

    observer.observe({ type: 'largest-contentful-paint', buffered: true })

    // If there was an active element, like a focused input, then we want to remember what it was so we can re-focus it
    // This is because Svelte's hydration removes all focus
    const activeElement = document.activeElement
    const selectionStart = activeElement ? activeElement.selectionStart : null
    const selectionEnd = activeElement ? activeElement.selectionEnd : null

    // Initialise Svelte which will remove all focus on active elements and re-initialise things like input values
    // We keep track of input values elsewhere before initialisation so we can automatically maintain user input
    ;(() => new App({
      target: document.getElementById('app'),
      hydrate: true,
      props: { platform, Entry },
    }))()

    platform.store.update(store => {
      store.appInitialised = true

      return store
    })

    for (const handler of platform.__eventHandlers.init || []) {
      await handler()
    }

    setTimeout(() => {
      if (activeElement) {
        if (activeElement.focus) {
          activeElement.focus()
        }

        if (activeElement.setSelectionRange) {
          // We use a try block here because certain input types, like email/number, do have the setSelectionRange
          // function, but don't actually work and throw an error
          try {
            activeElement.setSelectionRange(selectionStart, selectionEnd)
          } catch (err) {}
        }
      }

      initMutationObserver(platform)
    }, 0)
  }

  function onLeaveMessage (e) {
    const confirmationMessage = 'Are you sure you want to leave this page?\nAny unsaved changes will be lost.'

    e.returnValue = confirmationMessage

    return confirmationMessage
  }

  platform.store.subscribe(async store => {
    if (store.editor.actions.length || store.offlineActions.length || store.editor.onPageSaveFuncs.length || store.editor.clientActions.length) {
      window.onbeforeunload = onLeaveMessage
    } else {
      window.onbeforeunload = null
    }
  })

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js')
  }

  init()

  function initMutationObserver (platform) {
    const imgSrcMutationObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.target.tagName !== 'IMG' || mutation.attributeName !== 'src') {
          continue
        }

        setImgSrc(mutation.target)
      }
    })

    for (const img of Array.from(document.querySelectorAll('[loading="lazy"], [optimise="true"]'))) {
      imgSrcMutationObserver.observe(img, {
        attributeOldValue: true,
      })

      if (img.complete) {
        setImgSrc(img)
      } else {
        img.removeEventListener('load', setImgSrc)
        img.addEventListener('load', setImgSrc)
      }
    }

    const imgMutationObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        const addedImages = Array.from(mutation.addedNodes).filter(node => node.tagName === 'IMG')

        for (const img of addedImages) {
          imgSrcMutationObserver.observe(img, {
            attributeOldValue: true,
          })

          if (img.complete) {
            setImgSrc(img)
          } else {
            img.removeEventListener('load', setImgSrc)
            img.addEventListener('load', setImgSrc)
          }
        }
      }
    })

    imgMutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })

    window.addEventListener('resize', platform.api.core.debounce(() => {
      for (const img of Array.from(document.querySelectorAll('[loading="lazy"], [optimise="true"]'))) {
        if (img.complete) {
          setImgSrc(img)
        } else {
          img.removeEventListener('load', setImgSrc)
          img.addEventListener('load', setImgSrc)
        }
      }
    }, 300))
  }

  function setImgSrc (e) {
    const img = e.target ? e.target : e
    const lazySrc = img.getAttribute('src')
    const noOptimise = img.getAttribute('nooptimise') === 'true'

    if (!lazySrc || noOptimise || (img.getAttribute('loading') !== 'lazy' && img.getAttribute('optimise') !== 'true')) {
      return
    }

    const width = parseInt(img.parentNode.getBoundingClientRect().width, 10)
    const height = parseInt(img.parentNode.getBoundingClientRect().height, 10)
    const dim = width >= height ? width : height

    let size = ''

    switch (true) {
      case dim <= 150: size = 'w100'; break
      case dim <= 250: size = 'w200'; break
      case dim <= 360: size = 'w300'; break
      case dim <= 480: size = 'w400'; break
      case dim <= 600: size = 'w500'; break
      case dim <= 760: size = 'w650'; break
      case dim <= 960: size = 'w800'; break
      case dim <= 1200: size = 'w1000'; break
      case dim <= 1500: size = 'w1270'; break
      case dim <= 1900: size = 'w1600'; break
      case dim <= 2400: size = 'w2000'; break
      case dim <= 3000: size = 'w2500'; break
      default: size = 'w3200'; break
    }

    const newSrc = `${lazySrc.split('?').shift()}?size=${size}`

    if (img.getAttribute('src') !== newSrc) {
      img.setAttribute('src', newSrc)

      const captured = window.cms._captured.find(c => c.target === img)

      if (captured) {
        captured.value = newSrc
      } else {
        window.cms._captured.push({ target: img, value: newSrc })
      }
    }
  }
})()
